import { ExternalLink, Menu, MessageCircle, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { GithubMark } from "../../components/ui/GithubMark";
import { DISCORD_URL, GITHUB_URL } from "../../lib/links";
import { DOCS_SEARCH_INDEX, DOCS_SECTIONS, DOCS_TOC, docsPath } from "./docsNav";

export function DocsLayout() {
  const location = useLocation();
  const activeSlug = location.pathname.replace(/^\/docs\/?/, "");
  const activeSection = DOCS_SECTIONS.find((section) =>
    section.items.some((item) => item.slug === activeSlug)
  );
  const activeItem = activeSection?.items.find((item) => item.slug === activeSlug);
  const toc = DOCS_TOC[activeSlug];

  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [navigationOpen, setNavigationOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleKeydown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    }
    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, []);

  const trimmedQuery = query.trim().toLowerCase();
  const results = trimmedQuery
    ? DOCS_SEARCH_INDEX.filter((entry) =>
        `${entry.label} ${entry.section}`.toLowerCase().includes(trimmedQuery)
      )
    : [];

  function closeSearch() {
    setQuery("");
    setSearchOpen(false);
  }

  return (
    <div className="docs-shell">
      <aside className="docs-sidebar">
        <button
          type="button"
          className="docs-sidebar-toggle"
          aria-expanded={navigationOpen}
          aria-controls="docs-sidebar-content"
          onClick={() => setNavigationOpen((open) => !open)}
        >
          {navigationOpen ? (
            <X size={16} strokeWidth={1.75} absoluteStrokeWidth aria-hidden="true" />
          ) : (
            <Menu size={16} strokeWidth={1.75} absoluteStrokeWidth aria-hidden="true" />
          )}
          {navigationOpen ? "Close documentation navigation" : "Browse documentation"}
        </button>

        <div
          id="docs-sidebar-content"
          className={
            navigationOpen
              ? "docs-sidebar-content docs-sidebar-content--open"
              : "docs-sidebar-content"
          }
        >
          <div
            className="docs-search-wrap"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                setSearchOpen(false);
              }
            }}
          >
            <label className="docs-search">
              <Search size={16} strokeWidth={1.75} absoluteStrokeWidth aria-hidden="true" />
              <input
                ref={searchInputRef}
                type="text"
                className="docs-search-input"
                placeholder="Search documentation"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    closeSearch();
                    searchInputRef.current?.blur();
                  }
                }}
                aria-label="Search documentation"
                aria-expanded={searchOpen && Boolean(trimmedQuery)}
                aria-controls={searchOpen && trimmedQuery ? "docs-search-results" : undefined}
              />
              <kbd className="docs-search-kbd" aria-hidden="true">
                ⌘/Ctrl K
              </kbd>
            </label>

            {searchOpen && trimmedQuery ? (
              <ul id="docs-search-results" className="docs-search-results">
                {results.length > 0 ? (
                  results.map((entry) => (
                    <li key={entry.slug}>
                      <Link
                        to={docsPath(entry.slug)}
                        className="docs-search-result"
                        onClick={() => {
                          closeSearch();
                          setNavigationOpen(false);
                        }}
                      >
                        <span className="docs-search-result-label">{entry.label}</span>
                        <span className="docs-search-result-section">{entry.section}</span>
                      </Link>
                    </li>
                  ))
                ) : (
                  <li className="docs-search-empty">No results for &ldquo;{query.trim()}&rdquo;</li>
                )}
              </ul>
            ) : null}
          </div>

          <nav aria-label="Documentation">
            {DOCS_SECTIONS.map((section) => (
              <div className="docs-nav-section" key={section.label}>
                <p className="docs-nav-heading">{section.label}</p>
                <ul className="docs-nav-list">
                  {section.items.map((item) => (
                    <li key={item.slug}>
                      <NavLink
                        to={docsPath(item.slug)}
                        end={item.slug === ""}
                        className={({ isActive }) =>
                          isActive ? "docs-nav-link docs-nav-link--active" : "docs-nav-link"
                        }
                        onClick={() => setNavigationOpen(false)}
                      >
                        <item.icon
                          size={16}
                          strokeWidth={1.75}
                          absoluteStrokeWidth
                          aria-hidden="true"
                        />
                        <span className="docs-nav-label">{item.label}</span>
                        {item.status ? (
                          <span className="docs-nav-status">{item.status}</span>
                        ) : null}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>
      </aside>

      <div className="docs-main">
        <div className="docs-topbar">
          <p className="docs-breadcrumb">
            Docs
            {activeSection ? <> / {activeSection.label}</> : null}
          </p>
        </div>

        <div className="docs-content" key={activeItem?.slug ?? activeSlug}>
          <Outlet />
        </div>
      </div>

      <aside className="docs-toc">
        {toc ? (
          <div>
            <p className="docs-toc-heading">On this page</p>
            <ul className="docs-toc-list">
              {toc.map((entry, index) => (
                <li key={entry.id}>
                  <a
                    href={`#${entry.id}`}
                    className={
                      index === 0 ? "docs-toc-link docs-toc-link--active" : "docs-toc-link"
                    }
                  >
                    {entry.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="docs-help-box">
          <p className="docs-toc-heading">Need help?</p>
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noreferrer"
            className="docs-help-link"
            aria-label="Join the community (opens in a new tab)"
          >
            <MessageCircle size={16} strokeWidth={1.75} absoluteStrokeWidth aria-hidden="true" />
            Join the community
            <ExternalLink size={13} strokeWidth={2} absoluteStrokeWidth aria-hidden="true" />
          </a>
          <a
            href={GITHUB_URL}
            target="_blank"
            rel="noreferrer"
            className="docs-help-link"
            aria-label="View on GitHub (opens in a new tab)"
          >
            <GithubMark size={16} />
            View on GitHub
            <ExternalLink size={13} strokeWidth={2} absoluteStrokeWidth aria-hidden="true" />
          </a>
        </div>
      </aside>
    </div>
  );
}
